// src/services/api/facturacionElectronicaApi.ts
import api from '../../utils/axios';

export type AmbienteDian = 'SANDBOX' | 'PRODUCCION';

export type EstadoDocumento =
  | 'PENDIENTE'
  | 'ENVIADO'
  | 'VALIDADO'
  | 'RECHAZADO'
  | 'ERROR';

export interface ResolucionDian {
  Id: number;
  NumeroResolucion: string;
  Prefijo: string;
  RangoDesde: number;
  RangoHasta: number;
  NumeroActual: number;
  FechaDesde: string;
  FechaHasta: string;
  ClaveTecnica?: string;
  Ambiente: AmbienteDian;
  Activo: boolean;
}

export interface DocumentoElectronico {
  Id: number;
  IdVenta: number;
  Estado: EstadoDocumento;
  Prefijo?: string;
  NumeroDocumento?: string;
  NumeroResolucion?: string;
  CUFE?: string;
  QR?: string;
  UrlPdf?: string;
  UrlXml?: string;
  MensajeError?: string;
  Intentos: number;
  FechaValidacion?: string;
}

export interface ProveedorFe {
  Id: number;
  Codigo: string;
  Nombre: string;
  BaseUrl: string;
  Email: string | null;
  /** Token enmascarado; el valor real nunca sale del backend. */
  TokenEnmascarado: string | null;
  TienePassword: boolean;
  TieneClientId: boolean;
  RangoNumeracionId: number | null;
  Ambiente: AmbienteDian;
  Activo: boolean;
}

export interface ProveedorPayload {
  Id?: number;
  Codigo: string;
  Nombre: string;
  BaseUrl: string;
  /** Solo se envia cuando se quiere reemplazar. */
  Token?: string;
  Email?: string;
  Password?: string;
  /** Solo para proveedores OAuth2 (Factus). */
  ClientId?: string;
  ClientSecret?: string;
  /** Rango de numeracion cuando el proveedor lleva el consecutivo. */
  RangoNumeracionId?: number;
  Ambiente: AmbienteDian;
  Activo?: boolean;
}

export type ResolucionPayload = Omit<ResolucionDian, 'Id' | 'NumeroActual'> &
  Partial<Pick<ResolucionDian, 'NumeroActual'>>;

const BASE = '/facturacion-electronica';

export class FacturacionElectronicaApi {
  // ── Resoluciones ──────────────────────────────────────────────────────────
  static async listarResoluciones(): Promise<ResolucionDian[]> {
    const { data } = await api.post(`${BASE}/resoluciones/findall`, {});
    return data;
  }

  static async crearResolucion(dto: ResolucionPayload): Promise<ResolucionDian> {
    const { data } = await api.post(`${BASE}/resoluciones/crear`, dto);
    return data;
  }

  static async actualizarResolucion(
    id: number,
    dto: Partial<ResolucionPayload>,
  ): Promise<ResolucionDian> {
    const { data } = await api.post(`${BASE}/resoluciones/actualizar/${id}`, dto);
    return data;
  }

  static async eliminarResolucion(id: number): Promise<{ mensaje: string }> {
    const { data } = await api.post(`${BASE}/resoluciones/eliminar/${id}`, {});
    return data;
  }

  // ── Proveedores ───────────────────────────────────────────────────────────
  static async listarProveedores(): Promise<ProveedorFe[]> {
    const { data } = await api.post(`${BASE}/proveedores/findall`, {});
    return data;
  }

  /** Códigos con implementación disponible en el backend. */
  static async proveedoresDisponibles(): Promise<string[]> {
    const { data } = await api.post(`${BASE}/proveedores/disponibles`, {});
    return data;
  }

  static async guardarProveedor(dto: ProveedorPayload): Promise<ProveedorFe> {
    const { data } = await api.post(`${BASE}/proveedores/guardar`, dto);
    return data;
  }

  static async activarProveedor(id: number): Promise<ProveedorFe> {
    const { data } = await api.post(`${BASE}/proveedores/activar/${id}`, {});
    return data;
  }

  static async eliminarProveedor(id: number): Promise<{ mensaje: string }> {
    const { data } = await api.post(`${BASE}/proveedores/eliminar/${id}`, {});
    return data;
  }

  // ── Documentos electrónicos ───────────────────────────────────────────────
  /** Emite ante la DIAN la factura de una venta. */
  static async emitir(idVenta: number): Promise<DocumentoElectronico> {
    const { data } = await api.post(`${BASE}/emitir`, { idVenta });
    return data;
  }

  /** Estado de los documentos de varias ventas, en una sola petición. */
  static async estadosPorVentas(
    idsVenta: number[],
  ): Promise<Record<number, { Id: number; Estado: EstadoDocumento; CUFE: string | null }>> {
    if (!idsVenta.length) return {};
    const { data } = await api.post(`${BASE}/estados`, { idsVenta });
    return data;
  }

  /** Consulta el documento asociado a una venta (null si aún no se emitió). */
  static async consultar(idVenta: number): Promise<DocumentoElectronico | null> {
    const { data } = await api.post(`${BASE}/consultar`, { idVenta });
    return data;
  }

  static async reintentar(idDocumento: number): Promise<DocumentoElectronico> {
    const { data } = await api.post(`${BASE}/reintentar/${idDocumento}`, {});
    return data;
  }

  /** Regenera el PDF y el XML de un documento ya validado. */
  static async regenerarSoportes(idVenta: number): Promise<DocumentoElectronico> {
    const { data } = await api.post(`${BASE}/soportes`, { idVenta });
    return data;
  }
}

export default FacturacionElectronicaApi;
